<template>
    <div ref="formWrapperRef" class="hubspot-form-animated__wrapper">
        <VJoyFormFieldSkeleton v-if="!isFormReady" />
        <HubspotForm
            class="mkt-hubspot-form-animated"
            :class="{'mkt-hubspot-form-animated--focused': isFormFocused}"
            :region="props.region"
            :portal-id="props.portalId"
            :form-id="props.formId"
            :salesforce-id="props.salesforceId"
            @ready="onFormReady"
            @submit="onFormSubmit"
        />
    </div>
</template>
<script lang="ts" setup>
    import {ref, useTemplateRef} from 'vue';
    import {onBeforeUnmount} from '#imports';
    import {readonlyState} from '@malt/utils';
    import {onClickOutside} from '@malt/utils';
    import {VJoyFormFieldSkeleton} from '@maltjoy/core-vue';
    import HubspotForm, {type HubspotFormEvents, type HubspotFormProps} from './HubspotForm.vue';

    type HubspotFormAnimatedProps = HubspotFormProps & {
        visibleFieldName: string;
    };
    type HubspotFormAnimatedEvents = HubspotFormEvents & {
        (e: 'focusin'): void;
        (e: 'focusout'): void;
    };
    const props = defineProps<HubspotFormAnimatedProps>();
    const emit = defineEmits<HubspotFormAnimatedEvents>();

    const {isFormFocused, isFormReady, onFormFocusOut, onFormReady, onFormSubmit} = useFormAnimation();

    const formWrapperRef = useTemplateRef('formWrapperRef');
    onClickOutside(formWrapperRef, () => {
        onFormFocusOut();
    });

    function useFormAnimation() {
        const isFormFocused = ref(false);
        const isFormReady = ref(false);
        const formRef = ref<HTMLFormElement | null>(null);

        function onFormFocusIn() {
            isFormFocused.value = true;
            emit('focusin');
            formRef.value?.querySelector(`.hs-${props.visibleFieldName}`)?.classList.add('form-field--open');
        }

        function onFormFocusOut() {
            emit('focusout');
            isFormFocused.value = false;
            formRef.value?.querySelector(`.hs-${props.visibleFieldName}`)?.classList.remove('form-field--open');
        }

        function onFormReady(form: HTMLFormElement) {
            formRef.value = form;
            isFormReady.value = true;

            // Add a special class to the visible field to trigger the animation
            form.querySelector(`.hs-${props.visibleFieldName}`)?.classList.add('form-field--visible');
            form.addEventListener('focusin', onFormFocusIn);
            emit('ready', form);
        }

        function onFormSubmit(form: HTMLFormElement) {
            emit('submit', form);
        }

        onBeforeUnmount(() => {
            const form = formRef.value;
            if (form) {
                form.removeEventListener('focusin', onFormFocusIn);
            }
        });

        return {
            onFormReady,
            onFormSubmit,
            onFormFocusOut,
            isFormFocused: readonlyState<boolean>(isFormFocused),
            isFormReady: readonlyState<boolean>(isFormReady),
        };
    }
</script>
<style lang="scss">
    .hubspot-form.mkt-hubspot-form-animated {
        text-align: left;

        .hs-form-field:not(.hs-fieldtype-radio) label:not(.hs-error-msg) {
            display: none;
        }

        .form-field--visible:not(.form-field--open) {
            width: 100%;
        }

        .hs-main-font-element,
        .hs-main-font-element * {
            background: none !important;
        }

        // Animation section
        .hs-form-field:not(.form-field--visible),
        .hs-richtext,
        .hs_submit,
        .legal-consent-container,
        .actions {
            transition:
                max-height var(--joy-transition-duration-default) var(--joy-transition-timing-function),
                opacity var(--joy-transition-duration-default) var(--joy-transition-timing-function),
                margin-bottom var(--joy-transition-duration-default) var(--joy-transition-timing-function);
            transition-duration: 400ms;
        }

        &:not(&--focused) {
            .hs-form-field:not(.form-field--visible),
            .hs-richtext,
            .hs_submit,
            .legal-consent-container,
            .actions {
                max-height: 0 !important;
                opacity: 0;
                margin: 0;
                padding: 0;
                visibility: hidden;
                pointer-events: none;
            }

            .hs-form-field {
                margin-bottom: 0;
            }
        }

        &--focused {
            .hs-form-field:not(.form-field--visible),
            .hs-richtext,
            .hs_submit,
            .legal-consent-container,
            .actions {
                max-height: initial;
                opacity: 1;
                visibility: visible;
                pointer-events: auto;
            }
        }
    }
</style>
